/**
 * VALIDACIÓN DE DISPONIBILIDAD EN EL CARRITO
 * 
 * Este script verifica la disponibilidad de los rituales cuando el usuario
 * abre la página del carrito. Si un ritual ya no tiene disponibilidad,
 * muestra un mensaje de alerta.
 * 
 * INSTALACIÓN:
 * 1. Sube este archivo a Shopify (Assets)
 * 2. En tu theme.liquid o cart template, agrega:
 *    <script src="{{ 'cart-validation.js' | asset_url }}" defer></script>
 */

(function() {
  'use strict';

  // Configuración
  const CONFIG = {
    apiUrl: 'https://script.google.com/macros/s/AKfycbwAiesueIRQF3-gKjNXE5DvlOCmT4j-eh-7jxyB6_0R2atlS5XuKMpz9Fi3jRLIpvaW/exec',
    maxCupos: 8
  };

  // Mensajes en múltiples idiomas
  const MESSAGES = {
    es: {
      checking: 'Verificando disponibilidad de rituales...',
      unavailable: 'El ritual de las {time} para el {date} ya no tiene disponibilidad y fue eliminado de tu carrito.',
      partialAvailable: 'Solo quedan {available} cupos para el ritual de las {time} el {date}. Ajustamos la cantidad de {requested} a {available}.',
      removed: 'Se eliminaron {count} ritual(es) de tu carrito porque ya no tienen disponibilidad.',
      adjusted: 'Se ajustó la cantidad de rituales porque no había suficientes cupos.',
      error: 'No se pudo verificar la disponibilidad.',
      allGood: 'Todos los rituales tienen disponibilidad ✓',
      ritualBeforeEntry: 'El ritual de las {ritualTime} no es válido porque tu entrada a las Termas es a las {entryTime}. El ritual debe ser después de tu entrada.',
      notEnoughEntries: 'Tienes {ritualQty} ritual(es) a las {ritualTime} pero solo {eligibleEntries} entrada(s) pueden usar ese horario. El ritual debe ser después de la hora de entrada.',
      invalidSchedule: 'Horario inválido detectado',
      pleaseCorrect: 'Por favor, modifica tu carrito antes de continuar.'
    },
    en: {
      checking: 'Checking ritual availability...',
      unavailable: 'The {time} ritual for {date} is no longer available and was removed from your cart.',
      partialAvailable: 'Only {available} spots available for the {time} ritual on {date}. We adjusted the quantity from {requested} to {available}.',
      removed: '{count} ritual(s) were removed from your cart because they are no longer available.',
      adjusted: 'Ritual quantities were adjusted due to limited availability.',
      error: 'Could not verify availability.',
      allGood: 'All rituals are available ✓',
      ritualBeforeEntry: 'The {ritualTime} ritual is invalid because your Termas entry is at {entryTime}. The ritual must be after your entry.',
      notEnoughEntries: 'You have {ritualQty} ritual(s) at {ritualTime} but only {eligibleEntries} entry(ies) can use that time. The ritual must be after the entry time.',
      invalidSchedule: 'Invalid schedule detected',
      pleaseCorrect: 'Please modify your cart before continuing.'
    },
    pt: {
      checking: 'Verificando disponibilidade dos rituais...',
      unavailable: 'O ritual das {time} para {date} não está mais disponível e foi removido do seu carrinho.',
      partialAvailable: 'Apenas {available} vagas disponíveis para o ritual das {time} em {date}. Ajustamos a quantidade de {requested} para {available}.',
      removed: '{count} ritual(is) foram removidos do seu carrinho porque não estão mais disponíveis.',
      adjusted: 'As quantidades de rituais foram ajustadas devido à disponibilidade limitada.',
      error: 'Não foi possível verificar a disponibilidade.',
      allGood: 'Todos os rituais estão disponíveis ✓',
      ritualBeforeEntry: 'O ritual das {ritualTime} é inválido porque sua entrada nas Termas é às {entryTime}. O ritual deve ser após sua entrada.',
      notEnoughEntries: 'Você tem {ritualQty} ritual(is) às {ritualTime} mas apenas {eligibleEntries} entrada(s) podem usar esse horário. O ritual deve ser após o horário de entrada.',
      invalidSchedule: 'Horário inválido detectado',
      pleaseCorrect: 'Por favor, modifique seu carrinho antes de continuar.'
    }
  };

  // Obtener idioma actual
  function getLocale() {
    const locale = window.Shopify?.locale || 'es';
    if (locale.startsWith('pt')) return 'pt';
    if (locale.startsWith('en')) return 'en';
    return 'es';
  }

  const t = MESSAGES[getLocale()];

  // Crear banner de alerta
  function createAlertBanner(message, type = 'warning') {
    // Remover banners anteriores
    const existingBanner = document.getElementById('ritual-availability-banner');
    if (existingBanner) existingBanner.remove();

    const banner = document.createElement('div');
    banner.id = 'ritual-availability-banner';
    banner.style.cssText = `
      padding: 15px 20px;
      margin: 15px 0;
      border-radius: 8px;
      font-size: 14px;
      line-height: 1.5;
      display: flex;
      align-items: center;
      gap: 10px;
      ${type === 'warning' ? `
        background: #FFF3CD;
        border: 1px solid #FFECB5;
        color: #856404;
      ` : type === 'error' ? `
        background: #F8D7DA;
        border: 1px solid #F5C6CB;
        color: #721C24;
      ` : `
        background: #D4EDDA;
        border: 1px solid #C3E6CB;
        color: #155724;
      `}
    `;

    const icon = type === 'warning' ? '⚠️' : type === 'error' ? '❌' : '✅';
    banner.innerHTML = `<span style="font-size: 20px;">${icon}</span><span>${message}</span>`;

    // Insertar al inicio del carrito - buscar múltiples selectores
    const cartContainer = document.querySelector('form[action="/cart"]') || 
                          document.querySelector('.cart') ||
                          document.querySelector('[data-cart]') ||
                          document.querySelector('.cart-items') ||
                          document.querySelector('.cart__items') ||
                          document.querySelector('.cart-wrapper') ||
                          document.querySelector('#cart') ||
                          document.querySelector('[class*="cart"]') ||
                          document.querySelector('main') ||
                          document.body;
    
    if (cartContainer) {
      cartContainer.insertBefore(banner, cartContainer.firstChild);
      // Scroll al banner para que sea visible
      banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    return banner;
  }

  // Obtener items del carrito
  async function getCartItems() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.items || [];
    } catch (error) {
      console.error('Error getting cart:', error);
      return [];
    }
  }

  // Eliminar un item del carrito
  async function removeFromCart(itemKey) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      });
      return response.ok;
    } catch (error) {
      console.error('Error removing from cart:', error);
      return false;
    }
  }

  // Actualizar cantidad de un item en el carrito
  async function updateCartItemQuantity(itemKey, newQuantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: newQuantity })
      });
      return response.ok;
    } catch (error) {
      console.error('Error updating cart:', error);
      return false;
    }
  }

  // Extraer fecha de las propiedades del item
  function extractDate(properties) {
    if (!properties) return null;
    
    // Buscar en propiedades (puede ser array u objeto)
    if (Array.isArray(properties)) {
      const fechaProp = properties.find(p => 
        p.name?.toLowerCase() === 'fecha' || 
        p.name?.toLowerCase() === '_booking_date'
      );
      return fechaProp?.value || null;
    } else {
      return properties['Fecha'] || properties['_booking_date'] || null;
    }
  }

  // Extraer hora del ritual
  function extractRitualTime(properties, variantTitle) {
    if (!properties) return null;
    
    // Buscar en propiedades
    if (Array.isArray(properties)) {
      const horaProp = properties.find(p => 
        p.name?.toLowerCase().includes('hora ritual') ||
        p.name?.toLowerCase() === 'hora ritual'
      );
      if (horaProp?.value) return horaProp.value;
    } else if (properties['Hora Ritual']) {
      return properties['Hora Ritual'];
    }
    
    // Extraer de variant_title (ej: "15:00 - Ritual Patagónico")
    if (variantTitle) {
      const match = variantTitle.match(/(\d{1,2}:\d{2})/);
      if (match) return match[1];
    }
    
    return null;
  }

  // Extraer hora de entrada de las propiedades
  function extractEntryTime(properties, variantTitle) {
    if (!properties) return null;
    
    // Buscar en propiedades (puede ser array u objeto)
    if (Array.isArray(properties)) {
      const horaProp = properties.find(p => 
        p.name?.toLowerCase() === 'hora' || 
        p.name?.toLowerCase() === 'hora entrada' ||
        p.name?.toLowerCase() === 'time'
      );
      if (horaProp?.value) {
        const match = horaProp.value.match(/(\d{1,2}:\d{2})/);
        return match ? match[1] : horaProp.value;
      }
    } else {
      const hora = properties['Hora'] || properties['Hora Entrada'] || properties['Time'];
      if (hora) {
        const match = hora.match(/(\d{1,2}:\d{2})/);
        return match ? match[1] : hora;
      }
    }
    
    // Extraer de variant_title (ej: "9:30 AV - Adulto")
    if (variantTitle) {
      const match = variantTitle.match(/(\d{1,2}:\d{2})/);
      if (match) return match[1];
    }
    
    return null;
  }

  // Convertir hora "HH:MM" a minutos desde medianoche
  function timeToMinutes(timeStr) {
    if (!timeStr) return null;
    const cleanTime = timeStr.replace(/[^\d:]/g, '').trim();
    const [hours, minutes] = cleanTime.split(':').map(Number);
    if (isNaN(hours)) return null;
    return hours * 60 + (minutes || 0);
  }

  // Bloquear el botón de checkout cuando hay errores
  function blockCheckout(errors) {
    const checkoutSelectors = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      'a[href*="/checkout"]',
      '.checkout-button',
      '#checkout',
      '[data-checkout]',
      'form[action="/cart"] button[type="submit"]'
    ];
    
    checkoutSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(button => {
        // Deshabilitar el botón
        button.disabled = true;
        button.style.opacity = '0.5';
        button.style.cursor = 'not-allowed';
        
        // Interceptar click
        button.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          
          const errorMessages = errors.map(err => err.message).join('\n\n');
          alert(`${t.invalidSchedule}\n\n${errorMessages}\n\n${t.pleaseCorrect}`);
          
          return false;
        }, true);
      });
    });
  }

  // Validar coherencia de horarios (ritual debe ser después de entrada)
  // También valida que la cantidad de rituales no exceda las entradas elegibles
  async function validateScheduleCoherence() {
    const items = await getCartItems();
    
    if (items.length === 0) return { valid: true, errors: [] };

    // Agrupar items por fecha
    const itemsByDate = {};
    
    items.forEach(item => {
      const titleLower = (item.title || '').toLowerCase();
      const variantTitle = item.variant_title || '';
      const properties = item.properties;
      const quantity = item.quantity || 1;
      
      const date = extractDate(properties);
      if (!date) return;
      
      if (!itemsByDate[date]) {
        itemsByDate[date] = { entries: [], rituals: [] };
      }
      
      // Detectar tipo de producto
      if (titleLower.includes('entrada') || titleLower.includes('entry') || titleLower.includes('ticket')) {
        const time = extractEntryTime(properties, variantTitle);
        if (time) {
          itemsByDate[date].entries.push({
            time,
            minutes: timeToMinutes(time),
            quantity,
            item
          });
        }
      } else if (titleLower.includes('ritual')) {
        const time = extractRitualTime(properties, variantTitle);
        if (time) {
          itemsByDate[date].rituals.push({
            time,
            minutes: timeToMinutes(time),
            quantity,
            item
          });
        }
      }
    });

    // Validar cada fecha
    const errors = [];
    
    Object.entries(itemsByDate).forEach(([date, dateItems]) => {
      if (dateItems.entries.length === 0 || dateItems.rituals.length === 0) return;
      
      // Para cada ritual, verificar cuántas entradas son elegibles (entraron antes del ritual - 30 min)
      dateItems.rituals.forEach(ritual => {
        if (ritual.minutes === null) return;
        
        // Contar cuántas entradas son elegibles para este horario de ritual
        // Una entrada es elegible si su hora + 30 min <= hora del ritual
        let entradasElegibles = 0;
        
        dateItems.entries.forEach(entry => {
          if (entry.minutes !== null && entry.minutes + 30 <= ritual.minutes) {
            entradasElegibles += entry.quantity;
          }
        });
        
        // Si no hay suficientes entradas elegibles para la cantidad de rituales
        if (entradasElegibles < ritual.quantity) {
          if (entradasElegibles === 0) {
            // No hay ninguna entrada que permita este ritual
            const earliestEntry = dateItems.entries.reduce((min, entry) => 
              (entry.minutes !== null && (min.minutes === null || entry.minutes < min.minutes)) ? entry : min
            , dateItems.entries[0]);
            
            errors.push({
              date,
              entryTime: earliestEntry.time,
              ritualTime: ritual.time,
              message: t.ritualBeforeEntry
                .replace('{ritualTime}', ritual.time)
                .replace('{entryTime}', earliestEntry.time)
            });
          } else {
            // Hay algunas entradas elegibles pero no suficientes
            errors.push({
              date,
              ritualTime: ritual.time,
              ritualQty: ritual.quantity,
              eligibleEntries: entradasElegibles,
              message: t.notEnoughEntries
                ? t.notEnoughEntries
                    .replace('{ritualTime}', ritual.time)
                    .replace('{ritualQty}', ritual.quantity)
                    .replace('{eligibleEntries}', entradasElegibles)
                : `Tienes ${ritual.quantity} ritual(es) a las ${ritual.time} pero solo ${entradasElegibles} entrada(s) válida(s) para ese horario.`
            });
          }
        }
      });
    });

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // Verificar disponibilidad de un ritual
  async function checkRitualAvailability(fecha) {
    try {
      // Normalizar fecha a YYYY-MM-DD
      let fechaFormateada = fecha;
      if (fecha.includes('/')) {
        const parts = fecha.split('/');
        if (parts[0].length === 4) {
          fechaFormateada = fecha; // Ya es YYYY/MM/DD
        } else {
          // Asumir DD/MM/YYYY
          fechaFormateada = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }
      fechaFormateada = fechaFormateada.replace(/\//g, '-');

      const response = await fetch(`${CONFIG.apiUrl}?fecha=${fechaFormateada}`);
      const data = await response.json();
      
      if (data.success && data.disponibilidad) {
        return data.disponibilidad;
      }
      return null;
    } catch (error) {
      console.error('Error checking availability:', error);
      return null;
    }
  }

  // Validar todos los rituales del carrito
  async function validateCart() {
    const items = await getCartItems();
    
    // PRIMERO: Validar coherencia de horarios (ritual después de entrada)
    const scheduleResult = await validateScheduleCoherence();
    if (!scheduleResult.valid) {
      // Mostrar errores de horarios incoherentes
      const errorMessages = scheduleResult.errors.map(e => e.message).join('\n\n');
      const fullMessage = `${t.invalidSchedule}\n\n${errorMessages}\n\n${t.pleaseCorrect}`;
      
      // Mostrar banner
      createAlertBanner(fullMessage.replace(/\n/g, '<br>'), 'error');
      
      // También mostrar alert para asegurar que el usuario vea el mensaje
      alert(fullMessage);
      
      // Bloquear checkout
      blockCheckout(scheduleResult.errors);
      return;
    }
    
    // Filtrar solo rituales
    const ritualItems = items.filter(item => {
      const title = (item.title + ' ' + (item.variant_title || '')).toLowerCase();
      return title.includes('ritual');
    });

    if (ritualItems.length === 0) {
      console.log('No hay rituales en el carrito');
      return;
    }

    // Mostrar mensaje de verificación
    const banner = createAlertBanner(t.checking, 'warning');

    // Agrupar rituales por fecha y hora
    const ritualsByDateAndTime = {};
    
    ritualItems.forEach(item => {
      const fecha = extractDate(item.properties);
      const hora = extractRitualTime(item.properties, item.variant_title);
      
      if (fecha && hora) {
        const key = `${fecha}|${hora}`;
        if (!ritualsByDateAndTime[key]) {
          ritualsByDateAndTime[key] = {
            fecha,
            hora,
            cantidad: 0,
            items: []
          };
        }
        ritualsByDateAndTime[key].cantidad += item.quantity;
        ritualsByDateAndTime[key].items.push(item);
      }
    });

    // Verificar disponibilidad para cada fecha única
    const fechasUnicas = [...new Set(Object.values(ritualsByDateAndTime).map(r => r.fecha))];
    const disponibilidadPorFecha = {};
    
    for (const fecha of fechasUnicas) {
      disponibilidadPorFecha[fecha] = await checkRitualAvailability(fecha);
    }

    // Verificar cada grupo de rituales y tomar acción
    const acciones = [];
    let itemsEliminados = 0;
    let itemsAjustados = 0;
    
    for (const [key, ritual] of Object.entries(ritualsByDateAndTime)) {
      const disponibilidad = disponibilidadPorFecha[ritual.fecha];
      
      if (!disponibilidad) {
        continue; // No pudimos verificar, no hacer nada
      }
      
      const horaInfo = disponibilidad[ritual.hora];
      
      if (!horaInfo) {
        continue;
      }
      
      const disponibles = horaInfo.disponibles || 0;
      
      if (disponibles <= 0) {
        // NO HAY DISPONIBILIDAD - Eliminar todos los items de este horario
        for (const item of ritual.items) {
          const removed = await removeFromCart(item.key);
          if (removed) {
            itemsEliminados++;
            acciones.push(t.unavailable
              .replace('{time}', ritual.hora)
              .replace('{date}', ritual.fecha));
          }
        }
      } else if (disponibles < ritual.cantidad) {
        // HAY MENOS DE LO SOLICITADO - Ajustar cantidad
        // Solo ajustamos el primer item, eliminamos el resto si hay varios
        let cuposRestantes = disponibles;
        
        for (const item of ritual.items) {
          if (cuposRestantes <= 0) {
            // Eliminar este item completamente
            await removeFromCart(item.key);
            itemsEliminados++;
          } else if (item.quantity > cuposRestantes) {
            // Reducir cantidad
            await updateCartItemQuantity(item.key, cuposRestantes);
            itemsAjustados++;
            acciones.push(t.partialAvailable
              .replace('{available}', disponibles)
              .replace('{time}', ritual.hora)
              .replace('{date}', ritual.fecha)
              .replace('{requested}', ritual.cantidad));
            cuposRestantes = 0;
          } else {
            cuposRestantes -= item.quantity;
          }
        }
      }
    }

    // Mostrar resultados y recargar si hubo cambios
    if (itemsEliminados > 0 || itemsAjustados > 0) {
      const mensajes = [];
      
      if (itemsEliminados > 0) {
        mensajes.push(t.removed.replace('{count}', itemsEliminados));
      }
      if (itemsAjustados > 0) {
        mensajes.push(t.adjusted);
      }
      
      // Agregar detalles
      if (acciones.length > 0) {
        mensajes.push('');
        mensajes.push(...acciones);
      }
      
      const mensajeCompleto = mensajes.join('\n');
      
      // Mostrar alert
      alert(mensajeCompleto);
      
      // Recargar la página para mostrar el carrito actualizado
      window.location.reload();
    } else {
      // Todo está bien, remover el banner de verificación
      setTimeout(() => {
        const banner = document.getElementById('ritual-availability-banner');
        if (banner) banner.remove();
      }, 1500);
    }
  }

  // Ejecutar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', validateCart);
  } else {
    validateCart();
  }

  // También ejecutar cuando el carrito se actualice (si hay eventos)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, validateCart);
  }

})();
